/**
 * Kai feed-snapshot artifact types — mirror `tideway-kai/src/orchestrator/artifacts/feed_snapshot.rs`.
 */

import type { FeedEvent, ScopeDimension } from "@/types/feed";

export interface KaiFeedScope {
  dimension: ScopeDimension | string;
  key?: string | null;
  label: string;
}

export interface KaiFeedFilters {
  severity?: string | null;
  time_horizon?: string | null;
  event_type?: string | null;
  audience?: string | null;
}

export interface KaiFeedSnapshot {
  title?: string | null;
  scope: KaiFeedScope;
  filters: KaiFeedFilters;
  events: FeedEvent[];
  footnotes?: string[];
  open_in_feed_url: string;
}

export interface KaiFeedSnapshotRefSpec {
  id: string;
  artifact?: unknown;
}

const VALID_DIMENSIONS = new Set([
  "my",
  "booking",
  "customer",
  "sales_person",
  "operator",
  "agent",
  "carrier",
  "lane",
  "management",
]);

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function parseEvent(row: unknown): FeedEvent | null {
  if (!isRecord(row)) return null;
  const id = typeof row.id === "string" ? row.id.trim() : "";
  const eventType = typeof row.event_type === "string" ? row.event_type.trim() : "";
  if (!id || !eventType) return null;
  return row as unknown as FeedEvent;
}

/** Parse + validate a `feed_snapshot` fenced body or artifact payload. */
export function parseFeedSnapshotSpec(body: string): KaiFeedSnapshot | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!isRecord(parsed)) return null;
    const scopeRaw = parsed.scope;
    if (!isRecord(scopeRaw)) return null;
    const dimension =
      typeof scopeRaw.dimension === "string" ? scopeRaw.dimension.trim() : "";
    const label = typeof scopeRaw.label === "string" ? scopeRaw.label.trim() : "";
    if (!VALID_DIMENSIONS.has(dimension) || !label) return null;

    const openUrl =
      typeof parsed.open_in_feed_url === "string"
        ? parsed.open_in_feed_url.trim()
        : "";
    if (!openUrl) return null;

    const eventsRaw = Array.isArray(parsed.events) ? parsed.events : [];
    const events = eventsRaw
      .map(parseEvent)
      .filter((e): e is FeedEvent => e != null);

    const filtersRaw = isRecord(parsed.filters) ? parsed.filters : {};
    const footnotes = Array.isArray(parsed.footnotes)
      ? parsed.footnotes.filter(
          (f): f is string => typeof f === "string" && f.trim() !== "",
        )
      : [];

    return {
      title: typeof parsed.title === "string" ? parsed.title : null,
      scope: {
        dimension,
        key:
          typeof scopeRaw.key === "string" && scopeRaw.key.trim()
            ? scopeRaw.key.trim()
            : null,
        label,
      },
      filters: {
        severity:
          typeof filtersRaw.severity === "string" ? filtersRaw.severity : null,
        time_horizon:
          typeof filtersRaw.time_horizon === "string"
            ? filtersRaw.time_horizon
            : null,
        event_type:
          typeof filtersRaw.event_type === "string"
            ? filtersRaw.event_type
            : null,
        audience:
          typeof filtersRaw.audience === "string" ? filtersRaw.audience : null,
      },
      events,
      footnotes,
      open_in_feed_url: openUrl,
    };
  } catch {
    return null;
  }
}

/** Parse a compact `feed_snapshot_ref` fence body `{ "id": "..." }`. */
export function parseFeedSnapshotRefSpec(
  body: string,
): KaiFeedSnapshotRefSpec | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!isRecord(parsed)) return null;
    const id = typeof parsed.id === "string" ? parsed.id.trim() : "";
    if (!id) return null;
    return { id, artifact: parsed.artifact };
  } catch {
    return null;
  }
}
